import { App, Button, Checkbox, DatePicker, Form, Input, InputNumber, Radio, Select, Space, Switch, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { apiClient } from "../../api/apiClient";
import { checkGstin, getGstinCaptcha, lookupGstin, type GstinCheckResult } from "../../api/gstinApi";
import { getApiErrorMessage } from "../utils/apiError";
import { UI_DATE_FORMAT } from "../utils/date";
import { SmartLookupField } from "../lookup/SmartLookupField";
import { EntityFormShell } from "./EntityFormShell";
import { useEditData, useSave } from "./entityFormHooks";
import type { EntityFormComponentProps } from "./types";

export function PartyCompositeForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [gstStatus, setGstStatus] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<{ sessionId: string; image: string } | null>(null);
  const [gstLoading, setGstLoading] = useState(false);
  const partyType = Form.useWatch("type", form);
  const saveState = useSave("/party-composite", onSaved, onCancel, mode, id);
  useEditData(form, "/party-composite", mode, id);

  const fillFromGstin = (gstin: string, result: GstinCheckResult) => {
    const name = result.legalName || result.name || "";
    form.setFieldsValue({
      gstNumber: gstin,
      name: name || form.getFieldValue("name"),
      registrationType: "Regular",
      pan: gstin.length >= 12 ? gstin.slice(2, 12) : form.getFieldValue("pan"),
      addressLine1: result.address || form.getFieldValue("addressLine1"),
      country: form.getFieldValue("country") || "India"
    });
    setCaptcha(null);
    setGstStatus([
      name || "GSTIN found",
      result.status,
      result.constitution,
      result.source ? `Source: ${result.source}` : null
    ].filter(Boolean).join(" | "));
  };

  const autoFillGstin = async () => {
    const gstin = String(form.getFieldValue("gstNumber") ?? "").trim().toUpperCase();
    if (gstin.length !== 15) {
      message.warning("Enter a 15-character GSTIN first.");
      return;
    }
    setGstLoading(true);
    setGstStatus(null);
    try {
      const portalCaptcha = await getGstinCaptcha();
      if (!portalCaptcha?.sessionId || !portalCaptcha.captchaImage) {
        message.error("GST portal did not return a captcha.");
        return;
      }
      setCaptcha({ sessionId: portalCaptcha.sessionId, image: portalCaptcha.captchaImage });
      setGstStatus("Enter captcha to fetch the latest details from the GST portal.");
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to verify GSTIN"));
    } finally {
      setGstLoading(false);
    }
  };

  const submitCaptcha = async () => {
    const gstin = String(form.getFieldValue("gstNumber") ?? "").trim().toUpperCase();
    const captchaText = String(form.getFieldValue("gstCaptcha") ?? "").trim();
    if (!captcha || !captchaText) {
      message.warning("Enter the captcha text.");
      return;
    }
    setGstLoading(true);
    try {
      const result = await lookupGstin({ gstin, captcha: captchaText, sessionId: captcha.sessionId });
      fillFromGstin(gstin, { ...result, found: true });
      form.setFieldValue("gstCaptcha", undefined);
    } catch (err) {
      message.error(getApiErrorMessage(err, "GST portal lookup failed"));
      const portalCaptcha = await getGstinCaptcha().catch(() => null);
      if (portalCaptcha?.sessionId && portalCaptcha.captchaImage) {
        setCaptcha({ sessionId: portalCaptcha.sessionId, image: portalCaptcha.captchaImage });
        form.setFieldValue("gstCaptcha", undefined);
      }
    } finally {
      setGstLoading(false);
    }
  };

  const saveParty = (values: Record<string, unknown>) => {
    const bankAccounts = Array.isArray(values.bankAccounts)
      ? values.bankAccounts.filter((bank) => {
        const item = bank as Record<string, unknown>;
        return Object.values(item).some((value) => String(value ?? "").trim());
      })
      : [];
    saveState.save({ ...values, bankAccounts });
  };

  const currentPartyType = partyType ?? form.getFieldValue("type") ?? "Customer";
  const showCustomerOpening = currentPartyType === "Customer" || currentPartyType === "Organization";
  const showVendorOpening = currentPartyType === "Vendor" || currentPartyType === "Organization";

  return (
    <Form
      form={form}
      layout="vertical"
      className="party-composite-form"
      initialValues={{
        type: "Customer",
        registrationType: "Unregistered",
        country: "India",
        enable: true,
        customerOpeningBalance: 0,
        customerOpeningBalanceType: "Dr",
        vendorOpeningBalance: 0,
        vendorOpeningBalanceType: "Cr",
        ...initialValues
      }}
      onFinish={saveParty}
    >
      <EntityFormShell {...saveState}>
        <div className="party-form-section">
          <div className="party-form-section-title">Customer / Vendor Detail</div>
          <Form.Item name="type" label="Company Type" rules={[{ required: true }]}>
            <Radio.Group
              options={[
                { value: "Customer", label: "Customer" },
                { value: "Vendor", label: "Vendor" },
                { value: "Organization", label: "Customer / Vendor" }
              ]}
            />
          </Form.Item>
          <Form.Item name="gstNumber" label="GSTIN">
            <Space.Compact style={{ width: "100%" }}>
              <Input autoFocus maxLength={15} onChange={(event) => form.setFieldValue("gstNumber", event.target.value.toUpperCase())} />
              <Button loading={gstLoading} onClick={autoFillGstin}>Auto Fill</Button>
            </Space.Compact>
          </Form.Item>
          {captcha ? (
            <div className="gst-captcha-box">
              <button type="button" className="gst-captcha-image-button" onClick={autoFillGstin}>
                <img src={captcha.image} alt="GST captcha" />
              </button>
              <Form.Item name="gstCaptcha" label="Captcha" className="gst-captcha-input">
                <Space.Compact style={{ width: "100%" }}>
                  <Input autoComplete="off" />
                  <Button loading={gstLoading} onClick={submitCaptcha}>Submit</Button>
                </Space.Compact>
              </Form.Item>
            </div>
          ) : null}
          {gstStatus ? <Tag color="blue" className="party-form-status">{gstStatus}</Tag> : null}
          <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="contactPerson" label="Contact Person"><Input /></Form.Item>
          <Form.Item name="phone" label="Contact No"><Input /></Form.Item>
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="registrationType" label="Registration Type">
            <Select options={[{ value: "Unregistered", label: "Unregistered" }, { value: "Regular", label: "Regular" }, { value: "Composite", label: "Composite" }]} />
          </Form.Item>
          <Form.Item name="pan" label="PAN"><Input /></Form.Item>
        </div>

        <div className="party-form-section">
          <div className="party-form-section-title">Billing Address</div>
          <Form.Item name="addressLine1" label="Address"><Input /></Form.Item>
          <Form.Item name="landmark" label="Landmark"><Input /></Form.Item>
          <Form.Item name="city" label="City"><Input /></Form.Item>
          <Form.Item name="country" label="Country"><Input /></Form.Item>
          <Form.Item name="state" label="State"><Input /></Form.Item>
          <Form.Item name="pincode" label="Pincode"><Input /></Form.Item>
        </div>

        <div className="party-form-section">
          <div className="party-form-section-title">Opening Balance</div>
          {showCustomerOpening ? (
            <>
              <div className="party-form-inline">
                <Form.Item name="customerOpeningBalance" label="Customer Balance">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="customerOpeningBalanceType" label=" ">
                  <Select options={[{ value: "Dr", label: "Debit" }, { value: "Cr", label: "Credit" }]} />
                </Form.Item>
              </div>
              <Typography.Text type={form.getFieldValue("customerOpeningBalanceType") === "Cr" ? "danger" : "success"}>
                Customer balance is tracked on the party ledger.
              </Typography.Text>
            </>
          ) : null}
          {showVendorOpening ? (
            <>
              <div className="party-form-inline">
                <Form.Item name="vendorOpeningBalance" label="Vendor Balance">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item name="vendorOpeningBalanceType" label=" ">
                  <Select options={[{ value: "Dr", label: "Debit" }, { value: "Cr", label: "Credit" }]} />
                </Form.Item>
              </div>
              <Typography.Text type={form.getFieldValue("vendorOpeningBalanceType") === "Dr" ? "success" : "danger"}>
                Vendor balance is tracked on the party ledger.
              </Typography.Text>
            </>
          ) : null}
        </div>

        <div className="party-form-section">
          <div className="party-form-section-title">Bank Details</div>
          <Form.List name="bankAccounts">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: "100%" }} size={8}>
                {fields.map((field) => (
                  <div className="party-bank-row" key={field.key}>
                    <Form.Item {...field} name={[field.name, "bankName"]} label="Bank">
                      <Input />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, "accountNumber"]} label="Bank A/C Number">
                      <Input />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, "ifsc"]} label="IFSC Code">
                      <Input />
                    </Form.Item>
                    <Button onClick={() => remove(field.name)}>Remove</Button>
                  </div>
                ))}
                <Button onClick={() => add({ isPrimary: fields.length === 0 })}>Add Bank Detail</Button>
              </Space>
            )}
          </Form.List>
        </div>

        <div className="party-form-section">
          <div className="party-form-section-title">Additional Details</div>
          <Form.Item name="faxNo" label="Fax No"><Input /></Form.Item>
          <Form.Item name="website" label="Website"><Input /></Form.Item>
          <Form.Item name="note" label="Note"><Input.TextArea rows={2} /></Form.Item>
          <Form.Item name="enable" valuePropName="checked">
            <Checkbox>Company will be visible on all document.</Checkbox>
          </Form.Item>
        </div>
      </EntityFormShell>
    </Form>
  );
}

export function ShortRemarkForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/short-remarks", onSaved, onCancel, mode, id);
  useEditData(form, "/short-remarks", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Short Remark" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function BankForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/banks", onSaved, onCancel, mode, id);
  useEditData(form, "/banks", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Bank Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="ifsc" label="IFSC"><Input /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function BankAccountForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const facilityType = Form.useWatch("facilityType", form);
  const saveState = useSave("/banks/accounts", onSaved, onCancel, mode, id);
  useEffect(() => {
    if (mode !== "edit" || !id) return;
    apiClient.get(`/banks/accounts/${id}`).then(({ data }) => {
      const value = data?.data ?? data;
      form.setFieldsValue({
        ...value,
        sanctionDate: value?.sanctionDate ? dayjs(value.sanctionDate) : undefined,
        expiryDate: value?.expiryDate ? dayjs(value.expiryDate) : undefined
      });
    }).catch(() => undefined);
  }, [form, id, mode]);
  const hasOverdraft = facilityType === "Overdraft" || facilityType === "CashCredit";
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ isActive: true, facilityType: "Normal", ...initialValues }}
      onFinish={(values) => saveState.save({
        ...values,
        sanctionDate: values.sanctionDate?.format?.("YYYY-MM-DD") ?? values.sanctionDate,
        expiryDate: values.expiryDate?.format?.("YYYY-MM-DD") ?? values.expiryDate
      })}
    >
      <EntityFormShell {...saveState}>
        <Form.Item name="bankId" label="Bank" rules={[{ required: true }]}>
          <SmartLookupField lookupKey="bank" onChange={(v) => form.setFieldValue("bankId", v.id)} />
        </Form.Item>
        <Form.Item name="accountName" label="Account Name" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="openingBalance" label="Opening Balance"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="facilityType" label="Facility Type">
          <Select
            options={[
              { value: "Normal", label: "Normal" },
              { value: "Overdraft", label: "Overdraft" },
              { value: "CashCredit", label: "Cash Credit" }
            ]}
          />
        </Form.Item>
        {hasOverdraft ? (
          <>
            <Form.Item name="overdraftLimit" label="Overdraft Limit" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="drawingPower" label="Drawing Power"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="interestRate" label="Interest Rate %"><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="sanctionDate" label="Sanction Date"><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="expiryDate" label="Expiry Date"><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
            <Form.Item name="allowOverLimit" label="Allow Over Limit" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="overLimitTolerance" label="Over-limit Tolerance"><InputNumber min={0} style={{ width: "100%" }} /></Form.Item>
          </>
        ) : null}
      </EntityFormShell>
    </Form>
  );
}

export function AccountGroupForm({ mode, id, initialValues, parentContext, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/ledgers", onSaved, onCancel, mode, id);
  useEditData(form, "/ledgers", mode, id);
  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{ isSystemLedger: false, accountType: parentContext?.accountType, ...initialValues }}
      onFinish={saveState.save}
    >
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Group Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="accountType" label="Account Type" rules={[{ required: true }]}>
          <Select
            disabled={Boolean(parentContext?.accountType)}
            options={["Asset", "Liability", "Income", "Expense"].map((x) => ({ value: x, label: x }))}
          />
        </Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function AccountLedgerForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/ledgers", onSaved, onCancel, mode, id);
  useEditData(form, "/ledgers", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Ledger Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="accountType" label="Account Type" rules={[{ required: true }]}>
          <Select options={["Asset", "Liability", "Income", "Expense"].map((x) => ({ value: x, label: x }))} />
        </Form.Item>
        <Form.Item name="parentLedgerId" label="Under Group">
          <SmartLookupField lookupKey="accountGroup" onChange={(v) => form.setFieldValue("parentLedgerId", v.id)} />
        </Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function EntryCategoryForm({ initialValues, parentContext, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/ledgers/entry-category", onSaved, onCancel);
  const accountType = Form.useWatch("accountType", form);
  const parentLedgerId = Form.useWatch("parentLedgerId", form);

  useEffect(() => {
    form.setFieldValue("parentLedgerId", undefined);
    form.setFieldValue("underLedgerId", undefined);
  }, [accountType, form]);

  useEffect(() => {
    form.setFieldValue("underLedgerId", undefined);
  }, [parentLedgerId, form]);

  return (
    <Form form={form} layout="vertical" initialValues={{ accountType: parentContext?.transactionType === "Credit" ? "Income" : "Expense", ...initialValues }} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Entry Category" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="accountType" label="Account Type" rules={[{ required: true }]}>
          <Select options={[{ value: "Expense", label: "Expense" }, { value: "Income", label: "Income" }]} />
        </Form.Item>
        <Form.Item name="parentLedgerId" label="Parent Group" rules={[{ required: true }]}>
          <SmartLookupField
            lookupKey="accountGroup"
            parentContext={accountType ? { accountType } : undefined}
            onChange={(v) => form.setFieldValue("parentLedgerId", v.id)}
          />
        </Form.Item>
        <Form.Item name="underLedgerId" label="Under Ledger">
          <SmartLookupField
            lookupKey="childLedger"
            parentContext={parentLedgerId ? { parentLedgerId } : undefined}
            onChange={(v) => form.setFieldValue("underLedgerId", v.id)}
          />
        </Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked" initialValue>
          <Switch />
        </Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function SacCodeForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const endpoint = mode === "edit" && id ? `/sac-codes/update/${id}` : "/sac-codes/create";
  const saveState = useSave(endpoint, onSaved, onCancel, mode, id);
  useEditData(form, "/sac-codes/detail", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="sacCode" label="SAC Code" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="cgstRate" label="CGST %" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="sgstRate" label="SGST %" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="igstRate" label="IGST %" rules={[{ required: true }]}><InputNumber min={0} max={100} style={{ width: "100%" }} /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function FinancialYearForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const endpoint = mode === "edit" && id ? `/financial-years/update/${id}` : "/financial-years/create";
  const saveState = useSave(endpoint, onSaved, onCancel, mode, id);
  useEffect(() => {
    if (mode !== "edit" || !id) return;
    apiClient.get(`/financial-years/detail/${id}`).then(({ data }) => {
      const value = data?.data ?? data;
      form.setFieldsValue({
        ...value,
        startDate: value?.startDate ? dayjs(value.startDate) : undefined,
        endDate: value?.endDate ? dayjs(value.endDate) : undefined
      });
    });
  }, [form, id, mode]);
  return (
    <Form form={form} layout="vertical" initialValues={{ isActive: true, isClosed: false, ...initialValues }} onFinish={(values) => saveState.save({
      ...values,
      startDate: values.startDate?.format("YYYY-MM-DD"),
      endDate: values.endDate?.format("YYYY-MM-DD")
    })}>
      <EntityFormShell {...saveState}>
        <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="startDate" label="Start Date" rules={[{ required: true }]}><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="endDate" label="End Date" rules={[{ required: true }]}><DatePicker format={UI_DATE_FORMAT} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
        <Form.Item name="isClosed" label="Closed" valuePropName="checked"><Switch /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function OrganizationForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [gstStatus, setGstStatus] = useState<string | null>(null);
  const saveState = useSave("/organizations", onSaved, onCancel, mode, id);
  useEditData(form, "/organizations", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={{ accountingProfile: {}, ...initialValues }} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="name" label="Company Name" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="gstNumber" label="GST Number">
          <Space.Compact style={{ width: "100%" }}>
            <Input />
            <Button onClick={async () => {
              const gstin = form.getFieldValue("gstNumber");
              try {
                const result = await checkGstin(gstin);
                setGstStatus(result.found ? `${result.legalName ?? result.name ?? "Found"} · ${result.status ?? ""}` : "Not found locally");
                if (result.address) form.setFieldValue("addressLine1", result.address);
              } catch (err) {
                message.error(getApiErrorMessage(err, "Unable to verify GSTIN"));
              }
            }}>Verify</Button>
          </Space.Compact>
        </Form.Item>
        {gstStatus ? <Tag color="blue">{gstStatus}</Tag> : null}
        <Form.Item name="pan" label="PAN"><Input /></Form.Item>
        <Form.Item name="addressLine1" label="Address"><Input.TextArea rows={2} /></Form.Item>
        <Form.Item name="city" label="City"><Input /></Form.Item>
        <Form.Item name="state" label="State"><Input /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function UserForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/users", onSaved, onCancel, mode, id);
  useEditData(form, "/users", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={{ roles: ["Staff"], ...initialValues }} onFinish={(values) => saveState.save({
      ...values,
      userId: id,
      ...(mode === "create" ? { password: values.password } : {})
    })}>
      <EntityFormShell {...saveState}>
        <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}><Input autoFocus /></Form.Item>
        <Form.Item name="firstName" label="First Name"><Input /></Form.Item>
        <Form.Item name="lastName" label="Last Name"><Input /></Form.Item>
        {mode === "create" ? <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}><Input.Password /></Form.Item> : null}
        {mode === "create" ? (
          <Form.Item name="roles" label="Roles" rules={[{ required: true }]}>
            <Select mode="multiple" options={["Admin", "Accountant", "Staff"].map((x) => ({ value: x, label: x }))} />
          </Form.Item>
        ) : null}
      </EntityFormShell>
    </Form>
  );
}

export function TaxLedgerSetupForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/org-tax-setup", onSaved, onCancel, "edit", id);
  useEffect(() => {
    apiClient.get("/org-tax-setup").then(({ data }) => form.setFieldsValue(data?.data ?? data ?? {}));
  }, [form]);
  return (
    <Form form={form} layout="vertical" initialValues={initialValues} onFinish={(values) => apiClient.put("/org-tax-setup", values).then(({ data }) => onSaved(data?.data ?? data))}>
      <EntityFormShell {...saveState}>
        {[
          ["cgstLedgerId", "CGST Ledger"],
          ["sgstLedgerId", "SGST Ledger"],
          ["igstLedgerId", "IGST Ledger"],
          ["tdsLedgerId", "TDS Ledger"]
        ].map(([name, label]) => (
          <Form.Item key={name} name={name} label={label} rules={[{ required: true }]}>
            <SmartLookupField lookupKey="accountLedger" onChange={(v) => form.setFieldValue(name, v.id)} />
          </Form.Item>
        ))}
      </EntityFormShell>
    </Form>
  );
}

export function LineItemTemplateForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const endpoint = mode === "edit" && id ? `/line-items/update/${id}` : "/line-items/create";
  const saveState = useSave(endpoint, onSaved, onCancel, mode, id);
  useEditData(form, "/line-items/detail", mode, id);
  return (
    <Form form={form} layout="vertical" initialValues={{ unit: "Nos", displayOrder: 1, isRequired: false, ...initialValues }} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="code" label="Column Code" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="label" label="Column Label" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="unit" label="Unit"><Input /></Form.Item>
        <Form.Item name="displayOrder" label="Display Order"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="isRequired" label="Required" valuePropName="checked"><Switch /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}

export function BillUnitDefinitionForm({ mode, id, initialValues, onSaved, onCancel }: EntityFormComponentProps) {
  const [form] = Form.useForm();
  const saveState = useSave("/bill-unit-definitions", onSaved, onCancel, "create");
  return (
    <Form form={form} layout="vertical" initialValues={{ id: mode === "edit" ? id : 0, displayOrder: 1, isActive: true, ...initialValues }} onFinish={saveState.save}>
      <EntityFormShell {...saveState}>
        <Form.Item name="id" hidden><Input /></Form.Item>
        <Form.Item name="code" label="Code" rules={[{ required: true }]}><Input autoFocus /></Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item name="displayOrder" label="Display Order"><InputNumber min={1} style={{ width: "100%" }} /></Form.Item>
        <Form.Item name="isActive" label="Active" valuePropName="checked"><Switch /></Form.Item>
      </EntityFormShell>
    </Form>
  );
}
